import {
  Select,
  SelectOptionType,
  SingleSelectChangeEvent,
} from '@velocity/ui';
import React from 'react';
import FormFieldShell, { FormFieldShellProps } from './form-field-shell';

export interface FormSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FormSelectFieldProps extends FormFieldShellProps {
  options: ReadonlyArray<FormSelectOption>;
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  /** Enables the type-ahead search inside the dropdown (long lists, e.g. countries) */
  searchable?: boolean;
  dataTestId?: string;
}

/*
 * Velocity `FormField` + single `Select`, controlled so it can be used with
 * react-hook-form's `Controller`:
 *
 *   <Controller
 *     name='country_code'
 *     control={control}
 *     render={({ field, fieldState }) => (
 *       <FormSelectField label='Land' options={countries} {...field} errorMessage={fieldState.error?.message} />
 *     )}
 *   />
 */
const FormSelectField: React.FC<FormSelectFieldProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Bitte auswählen',
  hideLabel,
  errorMessage,
  hint,
  tooltip,
  required,
  disabled,
  readOnly,
  searchable = false,
  dataTestId,
  className,
}) => (
  <FormFieldShell
    {...{
      name,
      label,
      hideLabel,
      errorMessage,
      hint,
      tooltip,
      required,
      disabled,
      readOnly,
      className,
    }}
  >
    <Select<SelectOptionType>
      name={name}
      options={options as SelectOptionType[]}
      value={value ?? ''}
      placeholder={placeholder}
      advancedSearch={searchable}
      noOptionsLabel='Keine Treffer'
      onChange={(event: SingleSelectChangeEvent) =>
        onChange(event.target.value)
      }
      onBlur={onBlur}
      data-testid={dataTestId ?? `select-field-${name}`}
    />
  </FormFieldShell>
);

export default FormSelectField;
